import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { IconContext } from '@phosphor-icons/react';
import { config } from './config';
import { i18n } from './i18n';
import { oidcConfig } from './auth/oidc-config';
import { SessionCookieBridge } from './auth/SessionCookieBridge';
import { UnauthorizedBridge } from './auth/UnauthorizedBridge';
import { App } from './App';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { ConfirmDialogProvider } from './components/ConfirmDialog';
import { ToastProvider } from './components/Toast';
import { TooltipProvider } from './components/ui/tooltip';
import { VersionAutoReload } from './components/VersionAutoReload';
import { DevModeBanner } from './components/DevModeBanner';
import { initSentry } from './lib/sentry';
import { installGlobalErrorHandlers, setToken as setClientLogToken } from './lib/clientLog';
import './styles/global.css';
import './styles/fullcalendar.css';

// Initialise Sentry before any other code so the SDK captures errors thrown
// during module evaluation (e.g. an OIDC mis-config). Soft-disable: with no
// VITE_SENTRY_DSN set, init() is a no-op.
initSentry();

// Install window-level error / unhandledrejection listeners that forward
// to /api/v1/clientlogs (and the browser console). Mounted here so even a
// crash during module evaluation in a provider is caught. The listeners
// are no-ops until auth resolves; AuthTokenBridge below hands them the
// bearer token as soon as it's available.
installGlobalErrorHandlers();

// Transparent recovery from a deploy that landed while this tab was open.
// Vite fires `vite:preloadError` when a dynamically-imported chunk fails to
// load — almost always because a new build replaced the hashed asset this
// long-lived tab was about to fetch. Reload to pull the fresh bundle instead
// of surfacing a hard chunk-load error. A short time-based guard prevents a
// reload loop if the failure is something other than a stale deploy (e.g. a
// genuine network/CDN outage): if we already reloaded in the last 10s, let
// the error surface rather than spin.
window.addEventListener('vite:preloadError', () => {
  const KEY = 'sa:lastPreloadReload';
  const last = Number(window.sessionStorage.getItem(KEY) ?? '0');
  if (Date.now() - last < 10_000) return;
  window.sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

// E2E test mode: pre-seed sessionStorage with a synthetic OIDC User so that
// react-oidc-context reports isAuthenticated=true on first render and never
// makes a real call to Zitadel. The CI Playwright job builds the SPA with
// VITE_E2E_DEV_TOKEN set, and the matching API instance is started with
// TEST_MODE=true + the same E2E_DEV_TOKEN so the Bearer is accepted by the
// auth middleware. Production never sets VITE_E2E_DEV_TOKEN.
//
// The storage key matches oidc-client-ts's convention:
// `oidc.user:${authority}:${client_id}`. Authority + client_id come from
// config.ts which falls back to placeholder values in E2E mode.
if (config.e2eDevToken) {
  const key = `oidc.user:${config.authIssuer}:${config.authClientID}`;
  const expiresAt = Math.floor(Date.now() / 1000) + 24 * 60 * 60; // 24h
  const user = {
    id_token: config.e2eDevToken,
    access_token: config.e2eDevToken,
    token_type: 'Bearer',
    scope: 'openid profile email offline_access',
    profile: {
      sub: 'e2e-user-1',
      email: 'e2e@example.com',
      name: 'E2E Test User',
      preferred_username: 'e2e',
    },
    expires_at: expiresAt,
  };
  window.sessionStorage.setItem(key, JSON.stringify(user));
}

// Phosphor weight is set globally so individual icon usages don't have to
// repeat it. `bold` matches the Bricolage 700/800 headings; per-instance
// overrides are still allowed (e.g. `weight="fill"` for the eyebrow dot).
const phosphorContext = { weight: 'bold' as const };

const root = document.getElementById('root');
if (!root) {
  throw new Error('Missing #root element in index.html');
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// AuthTokenBridge hands the access token to the module-level clientLog
// helper whenever auth state changes. Lives inside AuthProvider so it
// can useAuth(); clientLog (which fires from global window listeners
// outside React) reads the same value without needing context access.
function AuthTokenBridge() {
  const auth = useAuth();
  useEffect(() => {
    setClientLogToken(auth.user?.access_token ?? null);
  }, [auth.user?.access_token]);
  return null;
}

createRoot(root).render(
  <StrictMode>
    {/* AppErrorBoundary wraps the entire tree so an uncaught render
        error (a) gets reported to Sentry and (b) shows a reload card
        instead of a blank page. Sits at the very outside so a crash
        in any provider — auth, query client, i18n — still renders
        the fallback. */}
    <AppErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <AuthProvider {...oidcConfig}>
          <AuthTokenBridge />
          <UnauthorizedBridge />
          {/* Trades the OIDC access token for a `.sportadmin.io` cookie
              after sign-in / silent renew so the Settings → App
              preview iframe at app.sportadmin.io picks up the same
              session. See auth/SessionCookieBridge.tsx. */}
          <SessionCookieBridge />
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <IconContext.Provider value={phosphorContext}>
                {/* ConfirmDialogProvider mounts a single <dialog> at the
                    app root; useConfirm() lets any component show a
                    designed confirmation prompt instead of window.confirm.
                    ToastProvider sits inside it so confirms launched from
                    toast callbacks (rare but valid) can still stack
                    correctly. */}
                <ConfirmDialogProvider>
                  <ToastProvider>
                    <TooltipProvider delayDuration={150} skipDelayDuration={300}>
                      <DevModeBanner />
                      <App />
                      {/* VersionAutoReload silently upgrades a long-lived tab
                          to a freshly deployed SPA build on the user's next
                          navigation — no "tap to refresh" prompt. Inside the
                          router so it can watch route changes. */}
                      <VersionAutoReload />
                    </TooltipProvider>
                  </ToastProvider>
                </ConfirmDialogProvider>
              </IconContext.Provider>
            </BrowserRouter>
          </QueryClientProvider>
        </AuthProvider>
      </I18nextProvider>
    </AppErrorBoundary>
  </StrictMode>,
);
